import React, { useState, useRef, forwardRef, useImperativeHandle, Children } from 'react';
import PropTypes from 'prop-types';
import { Toast } from 'primereact/toast';
import { isMobile } from 'react-device-detect';
import { WrappedButton } from '../ui/button/WrappedButton.component';
import Link from '../ui/Link.component';

/* How to call from Parent
    Call the show method from the parent component:
            toastRef.current.show({ toastType: 'info', summary: 'Success Message', detail: 'Order submitted' });

	positions - "center" | "bottom-right" | "bottom-left" | "top-right" | "top-left" | "top-center" | "bottom-center"

	toastType:'info' | 'success' | 'warning' | 'error'
	sticky: true | false

  */

const WrappedToast = forwardRef((props, ref) => {
	const [toastType, setToastType] = useState('info');
	const [position, setPosition] = useState('top-right');
	const toastRef = useRef(null);

	const getMobilePosition = positionValue =>
		positionValue.search('bottom') !== -1 ? 'bottom-center' : 'top-center';

	const getTransitionProps = originalPosition => {
		let pos = originalPosition;
		if (isMobile) {
			pos = getMobilePosition(originalPosition);
		}
		switch (pos) {
			case 'top-right':
			case 'bottom-right':
				return {
					classNames: {
						enter: 'slide-fade-in-right',
						exit: 'slide-fade-out-right',
					},
					timeout: 250,
				};
			case 'top-left':
			case 'bottom-left':
				return {
					classNames: {
						enter: 'slide-fade-in-left',
						exit: 'slide-fade-out-left',
					},
					timeout: 250,
				};
			case 'top-center':
				return {
					classNames: {
						enter: 'slide-fade-in-top',
						exit: 'slide-fade-out-top',
					},
					timeout: 250,
				};
			case 'bottom-center':
				return {
					classNames: {
						enter: 'slide-fade-in-bottom',
						exit: 'slide-fade-out-bottom',
					},
					timeout: 250,
				};
			default:
				return {
					classNames: {
						enter: 'slide-fade-in-right',
						exit: 'slide-fade-out-right',
					},
					timeout: 250,
				};
		}
	};

	// Validate that actions only contain WrappedButton or Link components
	const validateActions = actions => {
		if (!actions) return true;

		let isValid = true;

		// Iterate through children to check their types
		Children.forEach(actions.props.children, child => {
			// Skip null or undefined children
			if (!child) return;

			// Check if the child is a valid component type
			const isValidComponent =
				child.type === WrappedButton ||
				child.type === Link;

			if (!isValidComponent) {
				console.warn('Toast actions can only contain WrappedButton or Link components');
				isValid = false;
			}
		});

		return isValid;
	};

	const show = message => {
		const life = 10000; // 10 seconds
		const sticky = message.sticky !== undefined ? message.sticky : true;
		const messagePosition = message.position || 'top-right';

		// Extract button, link, and actions properties
		const { actions } = message;

		// Create content with optional button and link
		let content = message.detail;
		if (actions) {
			// Validate that actions only contain WrappedButton or Link components
			const isValidActions = validateActions(actions);

			content = (
				<div>
					<div>{message.detail}</div>

					{/* Render actions container if provided and valid */}
					{actions && isValidActions && (
						<div className="toast-actions-container">{actions}</div>
					)}
				</div>
			);
		}

		const toastProps = {
			...message,
			life,
			sticky,
			detail: content,
		};

		setToastType(message.toastType);
		setPosition(messagePosition);

		toastRef.current.show(toastProps);
	};

	// Expose the show method to parent components via ref
	useImperativeHandle(ref, () => ({
		show,
	}));

	const responsiveCls = isMobile ? 'mobile' : 'desktop';
	const mobilePosition = getMobilePosition(position);
	const transitionProps = getTransitionProps(position);

	return (
		<Toast
			className={`common-ui-toast ${toastType} ${responsiveCls}`}
			data-qa="ui-toast"
			ref={toastRef}
			position={isMobile ? mobilePosition : position}
			transitionOptions={transitionProps}
		/>
	);
});

// Add display name for debugging
WrappedToast.displayName = 'WrappedToast';

export default WrappedToast;
